import {
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';

export type Country = {
  /** ISO 3166-1 alpha-2 (e.g. 'AE', 'US'). */
  code: CountryCode;
  /** Localised display name via Intl.DisplayNames (e.g. "United Arab Emirates"). */
  name: string;
  /** Calling code without the leading '+', returned by libphonenumber as a string. */
  dial: string;
  /** Unicode regional indicator flag (best-effort emoji rendering — degrades to letters
   * on platforms that don't support flag emojis, which is acceptable for V1 Dubai. */
  flag: string;
};

const DEFAULT_COUNTRY: CountryCode = 'AE';

/** Top-of-list curated countries for the V1 Dubai market: GCC + Western/UK + major
 * buyer origins (India, Pakistan, Egypt). The full ISO list follows alphabetically. */
const PINNED_COUNTRIES: readonly CountryCode[] = [
  'AE',
  'SA',
  'QA',
  'KW',
  'BH',
  'OM',
  'GB',
  'US',
  'FR',
  'DE',
  'IT',
  'ES',
  'IN',
  'PK',
  'EG',
];

function codeToFlag(code: string): string {
  if (code.length !== 2) return '';
  const A = 0x1f1e6;
  const a = 'A'.charCodeAt(0);
  const first = String.fromCodePoint(A + (code.charCodeAt(0) - a));
  const second = String.fromCodePoint(A + (code.charCodeAt(1) - a));
  return `${first}${second}`;
}

/** Returns the project default country (V1 Dubai-only). Centralised so a future
 * geolocation-based override can be wired through a single seam. */
export function getDefaultCountry(): CountryCode {
  return DEFAULT_COUNTRY;
}

/**
 * Localised country list for the picker dropdown. Pinned countries come first
 * (V1 = Dubai market priorities), then the rest in alphabetical order by
 * localised name. Uses `Intl.DisplayNames` so AR/FR/EN all get native labels
 * for free — no per-country i18n keys to maintain.
 */
export function getCountryList(locale: string): Country[] {
  const display = new Intl.DisplayNames([locale], { type: 'region' });
  const all = getCountries();
  const enriched = all.map<Country>((code) => ({
    code,
    name: display.of(code) ?? code,
    dial: getCountryCallingCode(code),
    flag: codeToFlag(code),
  }));
  const pinnedSet = new Set(PINNED_COUNTRIES);
  const pinned = PINNED_COUNTRIES.map(
    (code) => enriched.find((c) => c.code === code) as Country,
  ).filter(Boolean);
  const rest = enriched
    .filter((c) => !pinnedSet.has(c.code))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
  return [...pinned, ...rest];
}

/**
 * Parses arbitrary phone input as a national number against `country` and
 * returns the canonical E.164 string (`+971501234567`). Returns `null` when
 * the input doesn't form a valid number — the caller can show an error
 * via Zod or directly.
 */
export function formatPhoneE164(value: string, country: CountryCode): string | null {
  if (!value || value.trim().length === 0) return null;
  const parsed = parsePhoneNumberFromString(value, country);
  if (!parsed || !parsed.isValid()) return null;
  return parsed.number;
}

/** Strict E.164 validation wrapper used by Zod refines (`/^\+[1-9]\d{1,14}$/`
 * already covers this, but libphonenumber tightens it against actual country
 * rules — keep both layers). */
export function isValidE164(value: string): boolean {
  if (!value) return false;
  if (!/^\+[1-9]\d{1,14}$/.test(value)) return false;
  return isValidPhoneNumber(value);
}

/**
 * Splits an E.164 string into `{ country, national }` so the picker can
 * restore the correct flag + dial code + national digits when the form is
 * re-mounted (e.g. profile edit, draft restore).
 */
export function splitE164(value: string | null | undefined): {
  country: CountryCode;
  national: string;
} {
  if (!value) return { country: DEFAULT_COUNTRY, national: '' };
  const parsed = parsePhoneNumberFromString(value);
  if (!parsed || !parsed.country) {
    return { country: DEFAULT_COUNTRY, national: value };
  }
  return { country: parsed.country, national: parsed.nationalNumber };
}
